// src/components/Map/WaybackControl.js
//
// Kleiner Overlay-Slider für Esri "Wayback" — historische Versionen des
// World-Imagery-Satellitenbilds (2026-07-01, Philips Wunsch: wenn die
// aktuelle Aufnahme Wolken/Schatten hat, ein anderes Aufnahmedatum wählen
// können).
//
// Wie es funktioniert:
//   * Esri veröffentlicht ~alle 2 Wochen einen "Release" des World-Imagery-
//     Mosaiks und hält ALLE alten Releases unter dem Wayback-WMTS-Dienst
//     abrufbar. Die Release-Liste (Nummer + Datum) liegt als JSON auf
//     config.maptiles.arcgis.com (CORS-offen, kein API-Key nötig — derselbe
//     Endpunkt, den Esris eigene Wayback-App benutzt).
//   * Der Slider tauscht nur die Tile-URL der bestehenden "imagery"-
//     Raster-Source (maplibre setTiles) — Zeichnen-Layer, Tools und
//     Projektion bleiben unberührt.
//   * Rechts = "Aktuell" (Live-Dienst), nach links = zurück in der Zeit.
//
// WICHTIG: Das Satellitenbild ist reine Zeichenhilfe — es geht NICHT in
// die Lichtberechnung ein. Der Regler ändert also nur die Optik.

import React, { useEffect, useMemo, useRef, useState } from "react";

const WAYBACK_CONFIG_URL =
  "https://s3-us-west-2.amazonaws.com/config.maptiles.arcgis.com/waybackconfig.json";

// Live-Dienst = bisheriger Default der App (MapInit.js).
const LIVE_TILES =
  "https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

export default function WaybackControl({ mapRef }) {
  // releases: aufsteigend nach Datum sortiert, [{ num, date, tiles }]
  const [releases, setReleases] = useState(null);
  // Slider-Wert: 0..releases.length; der Maximalwert steht für "Aktuell".
  const [idx, setIdx] = useState(0);
  const debounceRef = useRef(null);
  // Welche Auswahl aktuell auf der Karte liegt ("live" | Release-Nummer).
  // Verhindert den unnötigen Source-Swap beim Mount: der würde die gerade
  // ladenden Live-Kacheln abbrechen und graue Löcher hinterlassen.
  const appliedRef = useRef("live");

  // Release-Liste einmalig laden. Fehler → Control blendet sich aus
  // (Karte funktioniert dann einfach wie bisher ohne Zeitwahl).
  useEffect(() => {
    let cancelled = false;
    fetch(WAYBACK_CONFIG_URL)
      .then((r) => r.json())
      .then((cfg) => {
        if (cancelled) return;
        const parsed = Object.entries(cfg)
          .map(([num, item]) => {
            // itemTitle z.B. "World Imagery (Wayback 2024-06-13)"
            const m = /(\d{4}-\d{2}-\d{2})/.exec(item.itemTitle || "");
            if (!m || !item.itemURL) return null;
            return {
              num: Number(num),
              date: m[1],
              // WMTS-Template {level}/{row}/{col} → maplibre {z}/{y}/{x}
              tiles: item.itemURL
                .replace("{level}", "{z}")
                .replace("{row}", "{y}")
                .replace("{col}", "{x}"),
            };
          })
          .filter(Boolean)
          .sort((a, b) => (a.date < b.date ? -1 : 1));
        setReleases(parsed);
        setIdx(parsed.length); // Start = "Aktuell"
      })
      .catch(() => setReleases([])); // leeres Array = Control verstecken
    return () => { cancelled = true; };
  }, []);

  const current = useMemo(() => {
    if (!releases || idx >= releases.length) return null; // "Aktuell"
    return releases[idx];
  }, [releases, idx]);

  // Tile-Quelle tauschen — leicht entprellt, damit beim Ziehen des
  // Sliders nicht für jede Zwischenposition Tiles geladen werden.
  //
  // Warum remove+addSource statt setTiles: alte Wayback-Releases haben
  // KEINE hohen Zoomstufen (2014: max z17, ~2020: z18, aktuell: z19 —
  // per curl verifiziert). Ohne korrektes source.maxzoom fordert maplibre
  // z19-Kacheln an, bekommt 404 und zeigt Löcher. Mit maxzoom rendert es
  // stattdessen die tiefere Stufe hochskaliert (Overzoom) — leicht weicher,
  // aber nie leer. maxzoom lässt sich nur beim Anlegen der Source setzen.
  useEffect(() => {
    if (!releases) return;
    // Nichts zu tun, wenn die Auswahl schon auf der Karte liegt (v.a. beim
    // Mount: Auswahl "Aktuell" == Initialzustand aus MapInit).
    const targetKey = current ? String(current.num) : "live";
    if (targetKey === appliedRef.current) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const map = mapRef?.current;
      if (!map || !map.getSource || !map.getSource("imagery")) return;

      // konservative Zoom-Obergrenze je Aufnahmejahr (regional variiert
      // die Abdeckung; lieber Overzoom als leere Kacheln)
      let maxzoom = 19; // Live-Dienst
      if (current) {
        const year = Number(current.date.slice(0, 4));
        maxzoom = year >= 2023 ? 19 : year >= 2018 ? 18 : 17;
      }

      // Layer-Reihenfolge erhalten: imagery liegt zuunterst, alle
      // Zeichen-Layer darüber — beforeId = Layer direkt über imagery.
      const layers = map.getStyle().layers;
      const i = layers.findIndex((l) => l.id === "imagery");
      const beforeId = i >= 0 && layers[i + 1] ? layers[i + 1].id : undefined;

      map.removeLayer("imagery");
      map.removeSource("imagery");
      map.addSource("imagery", {
        type: "raster",
        tiles: [current ? current.tiles : LIVE_TILES],
        tileSize: 256,
        maxzoom,
      });
      map.addLayer({ id: "imagery", type: "raster", source: "imagery" }, beforeId);
      appliedRef.current = targetKey;
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [current, releases, mapRef]);

  if (!releases || releases.length === 0) return null;

  const label = current ? current.date : "Aktuell";

  return (
    <div
      style={{
        position: "absolute",
        left: 10,
        bottom: 10,
        zIndex: 5,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "4px 10px",
        background: "var(--surface-card, #fff)",
        border: "1px solid var(--border-default, #e5e7eb)",
        borderRadius: "var(--radius-pill, 999px)",
        boxShadow: "var(--shadow-sm, 0 1px 2px rgba(0,0,0,.15))",
        fontSize: 12,
        color: "var(--text-body, #111)",
        userSelect: "none",
      }}
      // Karte darf Slider-Interaktion nicht als Zeichen-Klick interpretieren
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span title="Satellitenbild-Aufnahme wählen (Esri Wayback) — nur Anzeige, keine Auswirkung auf die Berechnung">
        🛰
      </span>
      <input
        type="range"
        min={0}
        max={releases.length}
        step={1}
        value={idx}
        onChange={(e) => setIdx(Number(e.target.value))}
        style={{ width: 140, accentColor: "var(--brand-primary, #00e487)" }}
      />
      <span style={{ minWidth: 74, fontVariantNumeric: "tabular-nums" }}>
        {label}
      </span>
      {current && (
        <button
          onClick={() => setIdx(releases.length)}
          title="Zurück zum aktuellen Bild"
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 12,
            padding: 0,
            color: "var(--text-muted, #6b7280)",
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}
